import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_VideoToAudioConverter = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/video-to-audio-converter',
			name: 'Video to Audio Converter Online — Extract Audio Instantly by Lift',
			description:
				'Convert video to audio online — upload your video file and Lift extracts the audio track in seconds. Supports MP4, MOV, AVI, WEBM, MKV, and more. Try Lift now!',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/video-to-audio-converter#product' },
				{ '@id': 'https://liftapp.ai/video-to-audio-converter#howto' },
				{ '@id': 'https://liftapp.ai/video-to-audio-converter#faq' },
				{ '@id': 'https://liftapp.ai/video-to-audio-converter#why-lift' },
				{ '@id': 'https://liftapp.ai/video-to-audio-converter#explore-tools' },
				{ '@id': 'https://liftapp.ai/video-to-audio-converter#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/video-to-audio-converter#product',
			name: 'Video to Audio Converter Online',
			description:
				'Extract audio from any video file and save it as MP3, WAV, AAC, FLAC, and more. Upload your video, choose an audio output format, and download the result in seconds.',
			url: 'https://liftapp.ai/video-to-audio-converter',
			fileFormat: [
				'audio/mpeg',
				'audio/wav',
				'audio/aac',
				'audio/flac',
				'audio/ogg',
				'video/mp4',
				'video/quicktime',
				'video/x-msvideo',
				'video/webm',
				'video/x-matroska',
			],
			memoryRequirements: 'Max file size: 2GB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/video-to-audio-converter#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Video',
					text: 'Drop in any supported video format — MP4, MOV, AVI, MKV, WEBM, FLV, and more.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Choose Your Audio Output',
					text: 'Select from popular audio formats — MP3, WAV, AAC, FLAC, OGG, and more.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Lift Extracts the Audio',
					text: 'Lift processes your video and extracts the audio track with high-fidelity encoding, preserving the original quality.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download the Audio File',
					text: 'Your audio file is ready instantly — download it for playback, editing, podcasting, or sharing.',
				},
			],
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/video-to-audio-converter#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Video to Audio Converter?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Video to Audio Converter is an online tool that extracts audio from video files and saves it as MP3, WAV, AAC, FLAC, and more. Upload your video, choose an audio format, and download the result.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift Video to Audio Converter work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your video file in any supported format, select an audio output format (MP3, WAV, AAC, etc.), and Lift processes the file. Your audio file is ready to download in seconds.',
					},
				},
				{
					'@type': 'Question',
					name: 'How long does video to audio conversion take?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Most conversions complete in seconds. Processing time depends on file size and length, but Lift is optimized to deliver converted results as fast as possible.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will the extracted audio maintain its quality?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift uses high-fidelity encoding to preserve the original audio quality when extracting and converting from video.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to convert files with Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift prioritizes your privacy — all files are processed securely and are not stored or shared after conversion. Your converted result is available only to you.',
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-to-audio-converter#why-lift',
			name: "Why Lift's Video to Audio Converter stands out",
			description:
				'High-quality audio extraction, intelligent encoding, cross-platform support, and budget-friendly pricing — Lift delivers professional-grade audio conversion.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality audio',
					description:
						'AI-powered technology extracts and encodes audio at the highest quality.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Multiple audio formats',
					description:
						'Choose from MP3, WAV, AAC, FLAC, OGG, and more output formats.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description:
						'Available on iOS, Android, and Web for seamless access.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly software with an affordable and intuitive tool.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/images/video-to-audio-converter/whyLift.webp',
				name: 'Lift Video to Audio Converter delivering high-quality audio',
				description:
					"Lift's online video to audio converter in high resolution.",
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-to-audio-converter#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/video-to-audio-converter#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Video to Audio Converter',
					item: 'https://liftapp.ai/video-to-audio-converter',
				},
			],
		},
	],
};
